import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/AuthContext";
import { useLang } from "@/i18n";
import LoginForm from "@/components/LoginForm";
import StatusBadge from "@/components/StatusBadge";
import { Trash2, X } from "lucide-react";

export default function OwnerSpace() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("location");
  const [welcomeImage, setWelcomeImage] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setWelcomeImage(r.data.welcome_image || "")).catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      const p = window.location.pathname;
      if (!p.startsWith("/admin") && !p.startsWith("/espace-proprietaire")) logout();
    };
  }, [logout]);

  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") {
      api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
      api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    } else {
      api.get("/owner/properties").then((r) => setProperties(r.data)).catch(() => {});
      api.get("/owner/bookings").then((r) => setBookings(r.data)).catch(() => {});
    }
  }, [user]);

  if (user === null) {
    return <main className="flex min-h-screen items-center justify-center pt-20 text-maquis-soft">{t.auth.checking}</main>;
  }

  if (!user) {
    return (
      <main data-testid="owner-login-page" className="min-h-screen px-5 pb-24 pt-32 sm:pt-40">
        <LoginForm />
      </main>
    );
  }

  const serviceLabel = (k) => (t.services.items[k] ? t.services.items[k].title : k);
  const providerLabel = (k) => (t.providers.items[k] ? t.providers.items[k].title : k);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(t.admin.status_updated);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}`, { status });
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success(t.admin.status_updated);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteBooking = async (b) => {
    const code = window.prompt(t.admin.delete_code_prompt);
    if (code === null) return;
    if (code !== "190412") {
      toast.error(t.admin.delete_code_wrong);
      return;
    }
    if (!window.confirm(t.admin.demande_delete_confirm)) return;
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      setBookings((bs) => bs.filter((x) => x.id !== b.id));
      if (selected?.kind === "booking" && selected.item.id === b.id) setSelected(null);
      toast.success(t.admin.deleted);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteContact = async (c) => {
    if (!window.confirm(t.admin.message_delete_confirm)) return;
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      setContacts((cs) => cs.filter((x) => x.id !== c.id));
      if (selected?.kind === "contact" && selected.item.id === c.id) setSelected(null);
      toast.success(t.admin.deleted);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const locationContacts = contacts.filter((c) => c.kind === "location" || c.kind === "mixte");
  const gestionContacts = contacts.filter((c) => c.kind !== "location");
  const activeBookings = bookings.filter((b) => b.status !== "REFUSEE");
  const refusedBookings = bookings.filter((b) => b.status === "REFUSEE");

  const bookingCard = (b, readonly) => (
    <div
      key={b.id}
      data-testid={`owner-booking-${b.id}`}
      onClick={() => setSelected({ kind: "booking", item: b })}
      className="cursor-pointer rounded-md border border-maquis-deep/10 bg-casse p-6 transition-colors hover:border-maquis-deep/25"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-serif text-xl text-maquis-deep">{b.first_name} {b.last_name}</p>
          <p className="mt-1 text-xs text-maquis-soft">{b.email} · {b.phone}</p>
        </div>
        <StatusBadge status={b.status} />
      </div>
      <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-[11px] uppercase tracking-[0.15em] text-maquis-soft">{t.owner.th_dates}</dt>
          <dd className="mt-1 text-maquis-deep"><span className="text-[10px] uppercase tracking-wide text-maquis-soft">{t.admin.sim_arrival}</span> {b.arrival}</dd>
          <dd className="mt-0.5 text-maquis-deep"><span className="text-[10px] uppercase tracking-wide text-maquis-soft">{t.admin.sim_departure}</span> {b.departure}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-[0.15em] text-maquis-soft">{t.owner.th_property}</dt>
          <dd className="mt-1 text-maquis-deep">{b.property_name || "—"}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-[0.15em] text-maquis-soft">{t.owner.th_guests}</dt>
          <dd className="mt-1 text-maquis-deep">{b.guests}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-[0.15em] text-maquis-soft">{t.owner.th_services}</dt>
          <dd className="mt-1 text-maquis-deep">
            {[...b.services.map(serviceLabel), ...b.providers.map(providerLabel)].join(", ") || "—"}
          </dd>
        </div>
      </dl>
      {!readonly && b.message && (
        <p data-testid={`owner-booking-message-${b.id}`} className="mt-4 line-clamp-2 text-sm italic text-maquis-olive">
          « {b.message} »
        </p>
      )}

      {!readonly && (
        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-maquis-deep/10 pt-4" onClick={(e) => e.stopPropagation()}>
          <span className="text-[11px] uppercase tracking-[0.15em] text-maquis-soft">{t.owner.th_status}</span>
          <select
            data-testid={`owner-status-select-${b.id}`}
            value={b.status === "VALIDEE" || b.status === "REFUSEE" ? b.status : ""}
            onChange={(e) => e.target.value && updateStatus(b.id, e.target.value)}
            className="rounded-full border border-maquis-deep/25 bg-casse px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-maquis-deep transition-colors hover:border-maquis-deep"
          >
            <option value="" disabled>{t.owner.status_placeholder}</option>
            <option value="VALIDEE">{t.owner.accept}</option>
            <option value="REFUSEE">{t.owner.refuse}</option>
          </select>
          <button
            type="button"
            data-testid={`owner-booking-delete-${b.id}`}
            onClick={() => deleteBooking(b)}
            aria-label={t.admin.delete}
            className="ml-auto inline-flex items-center text-maquis-soft transition-colors duration-200 hover:text-red-900"
          >
            <Trash2 size={14} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );

  const contactCard = (c) => (
    <div
      key={c.id}
      data-testid={`owner-contact-${c.id}`}
      onClick={() => setSelected({ kind: "contact", item: c })}
      className="cursor-pointer rounded-md border border-maquis-deep/10 bg-casse p-6 transition-colors hover:border-maquis-deep/25"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-serif text-xl text-maquis-deep">{[c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || c.email}</p>
          <p className="mt-1 text-xs text-maquis-soft">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
        </div>
        <span className="rounded-full bg-ivoire px-3 py-1 text-[11px] uppercase tracking-[0.12em] text-maquis-olive">
          {t.owner.kind[c.kind || "autre"]}
        </span>
      </div>
      {(c.property_name || c.arrival || c.guests) && (
        <p className="mt-4 text-xs uppercase tracking-[0.12em] text-maquis-soft">
          {[c.property_name, c.arrival && c.departure ? `${c.arrival} → ${c.departure}` : null, c.guests ? `${c.guests} pers.` : null].filter(Boolean).join(" · ")}
        </p>
      )}
      {(c.property_type || c.sector) && (
        <p className="mt-4 text-xs uppercase tracking-[0.12em] text-maquis-soft">
          {[c.property_type ? t.types[c.property_type] : null, c.sector].filter(Boolean).join(" · ")}
        </p>
      )}
      <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-maquis-olive">{c.message}</p>
      <p className="mt-3 text-xs text-maquis-soft">{c.created_at?.slice(0, 10)}</p>
      <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-maquis-deep/10 pt-4" onClick={(e) => e.stopPropagation()}>
        <span className="text-[11px] uppercase tracking-[0.15em] text-maquis-soft">{t.owner.th_status}</span>
        <select
          data-testid={`owner-contact-status-${c.id}`}
          value={c.status === "VALIDEE" || c.status === "REFUSEE" ? c.status : ""}
          onChange={(e) => e.target.value && updateContactStatus(c.id, e.target.value)}
          className="rounded-full border border-maquis-deep/25 bg-casse px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-maquis-deep transition-colors hover:border-maquis-deep"
        >
          <option value="" disabled>{t.owner.status_placeholder}</option>
          <option value="VALIDEE">{t.owner.accept}</option>
          <option value="REFUSEE">{t.owner.refuse}</option>
        </select>
        <button
          type="button"
          data-testid={`owner-contact-delete-${c.id}`}
          onClick={() => deleteContact(c)}
          aria-label={t.admin.delete}
          className="ml-auto inline-flex items-center text-maquis-soft transition-colors duration-200 hover:text-red-900"
        >
          <Trash2 size={14} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );

  return (
    <main data-testid="owner-dashboard" className="min-h-screen bg-ivoire px-5 pb-24 pt-28 sm:px-8 sm:pt-36">
      <div className="mx-auto max-w-6xl">
        <section
          data-testid="owner-welcome-card"
          className={`relative overflow-hidden rounded-md border ${welcomeImage ? "border-transparent" : "border-maquis-deep/10 bg-casse"}`}
        >
          {welcomeImage && (
            <>
              <img src={welcomeImage} alt="" data-testid="owner-welcome-bg" className="absolute inset-0 h-full w-full scale-105 object-cover object-right sm:object-center brightness-[1.08]" />
              <div className="absolute inset-0 bg-mer/25" />
            </>
          )}
          <div className="relative flex min-h-[240px] sm:min-h-[620px] flex-wrap items-end justify-between gap-6 px-6 py-10 sm:px-10 sm:py-12">
            <div>
              <p className={`eyebrow ${welcomeImage ? "text-casse/85" : ""}`}>{t.owner.title}</p>
              <h1 className={`mt-2 font-serif text-2xl max-sm:whitespace-nowrap sm:text-4xl ${welcomeImage ? "text-casse" : "text-maquis-deep"}`}>
                {user.email === "caropv2a@gmail.com"
                  ? "Coucou Mamounette ♡"
                  : user.email === "margot.bourbasquet@hotmail.fr"
                    ? "Bonjour Margot !"
                    : `${t.owner.welcome} ${user.name || user.email}`}
              </h1>
            </div>
            <div className="flex gap-3">
              {isAdmin && (
                <Link
                  to="/admin"
                  data-testid="owner-admin-link"
                  className={`btn-outline-elegant !px-4 !py-2 !text-[10px] ${welcomeImage ? "border-casse/60 text-casse hover:bg-casse hover:text-maquis-deep" : "text-maquis-deep hover:bg-maquis-deep hover:text-casse"}`}
                >
                  {t.nav.admin}
                </Link>
              )}
            </div>
          </div>
        </section>

        {isAdmin ? (
          <>
            <div className="mt-10 flex flex-wrap gap-2" data-testid="owner-tabs">
              {[
                ["location", `${t.owner.tab_location} (${bookings.length + locationContacts.length})`],
                ["gestion", `${t.owner.tab_gestion} (${gestionContacts.length})`],
              ].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`owner-tab-${key}`}
                  onClick={() => setTab(key)}
                  className={`rounded-full border px-5 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${
                    tab === key
                      ? "border-maquis-deep bg-maquis-deep text-casse"
                      : "border-maquis-deep/25 text-maquis-deep/70 hover:border-maquis-deep"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "location" && (
              <section className="mt-8 space-y-5" data-testid="owner-location-section">
                {activeBookings.length === 0 && locationContacts.length === 0 ? (
                  <p data-testid="owner-no-location" className="text-sm text-maquis-soft">{t.owner.no_location}</p>
                ) : (
                  <>
                    {activeBookings.map((b) => bookingCard(b, false))}
                    {locationContacts.map(contactCard)}
                  </>
                )}
                {refusedBookings.length > 0 && (
                  <div className="pt-6" data-testid="owner-archives">
                    <button
                      data-testid="owner-archives-toggle"
                      onClick={() => setShowArchived((s) => !s)}
                      className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-maquis-soft transition-colors hover:text-maquis-deep"
                    >
                      {showArchived ? "▾" : "▸"} {t.owner.archives} ({refusedBookings.length})
                    </button>
                    {showArchived && (
                      <div className="mt-4 space-y-5 opacity-75" data-testid="owner-archives-list">
                        {refusedBookings.map((b) => bookingCard(b, false))}
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {tab === "gestion" && (
              <section className="mt-8 space-y-5" data-testid="owner-gestion-section">
                {gestionContacts.length === 0 ? (
                  <p data-testid="owner-no-gestion" className="text-sm text-maquis-soft">{t.owner.no_gestion}</p>
                ) : (
                  gestionContacts.map(contactCard)
                )}
              </section>
            )}
          </>
        ) : (
          <>
            <section className="mt-14">
              <h2 className="eyebrow">{t.owner.properties}</h2>
              {properties.length === 0 ? (
                <p data-testid="owner-no-properties" className="mt-6 text-sm text-maquis-soft">{t.owner.no_properties}</p>
              ) : (
                <div className="mt-6 grid gap-6 md:grid-cols-2">
                  {properties.map((p) => (
                    <div key={p.id} data-testid={`owner-property-${p.slug}`} className="rounded-md border border-maquis-deep/10 bg-casse p-6">
                      <div className="flex items-baseline justify-between gap-4">
                        <h3 className="font-serif text-2xl text-maquis-deep">{p.name}</h3>
                        <span className="text-[11px] uppercase tracking-[0.18em] text-maquis-soft">{t.types[p.type]}</span>
                      </div>
                      <p className="mt-1 text-sm text-maquis-soft">{p.location}</p>
                      <p className="mt-3 text-sm text-maquis-olive">
                        {p.guests} {t.locations_page.people} · {p.bedrooms} {t.property.bedrooms.toLowerCase()} · {p.pool ? t.locations_page.pool : t.locations_page.no_pool}
                      </p>
                      {p.blocked_dates && p.blocked_dates.length > 0 && (
                        <div className="mt-4">
                          <p className="text-[11px] uppercase tracking-[0.18em] text-maquis-soft">{t.owner.calendar}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {p.blocked_dates.map((d) => (
                              <span key={d} className="rounded-full bg-[#B08D57]/15 px-3 py-1 text-xs text-[#7A5A2B]">{d}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-14">
              <h2 className="eyebrow">{t.owner.bookings}</h2>
              {bookings.length === 0 ? (
                <p data-testid="owner-no-bookings" className="mt-6 text-sm text-maquis-soft">{t.owner.no_bookings}</p>
              ) : (
                <div className="mt-6 space-y-5" data-testid="owner-bookings-list">
                  {bookings.map((b) => bookingCard(b, true))}
                </div>
              )}
            </section>
          </>
        )}

        {selected && (
          <div
            data-testid="owner-detail-overlay"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-mer/70 p-4 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <div
              data-testid="owner-detail-modal"
              className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-md bg-casse p-8 shadow-[0_30px_80px_-30px_rgba(42,51,35,0.5)]"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const it = selected.item;
                const isBooking = selected.kind === "booking";
                const svc = isBooking
                  ? [...(it.services || []).map(serviceLabel), ...(it.providers || []).map(providerLabel)].join(", ")
                  : "";
                const mailSubject = isBooking || it.kind === "location" || it.kind === "mixte"
                  ? t.admin.mail_subject_location
                  : t.admin.mail_subject_gestion;
                const row = (label, value, testid) => (
                  <div key={testid} className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                    <dt className="text-maquis-soft">{label}</dt>
                    <dd data-testid={testid} className="text-right font-medium text-maquis-deep">{value}</dd>
                  </div>
                );
                return (
                  <>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="eyebrow">{isBooking ? t.admin.booking_details : t.owner.kind[it.kind || "autre"]}</p>
                        <h3 className="mt-2 font-serif text-2xl text-maquis-deep">
                          {[it.first_name, it.last_name].filter(Boolean).join(" ") || it.name || it.email}
                        </h3>
                      </div>
                      <button
                        data-testid="owner-detail-close"
                        onClick={() => setSelected(null)}
                        aria-label="Fermer"
                        className="rounded-full p-2 text-maquis-soft transition-colors hover:bg-ivoire hover:text-maquis-deep"
                      >
                        <X size={18} strokeWidth={1.75} />
                      </button>
                    </div>
                    <dl className="mt-6 space-y-3 text-sm">
                      {it.property_name ? row(t.owner.th_property, it.property_name, "owner-detail-property") : null}
                      {it.arrival ? row(t.owner.th_dates, `${it.arrival} → ${it.departure}`, "owner-detail-dates") : null}
                      {it.guests ? row(t.owner.th_guests, String(it.guests), "owner-detail-guests") : null}
                      {svc ? row(t.owner.th_services, svc, "owner-detail-services") : null}
                      {!isBooking && (it.property_type || it.sector)
                        ? row(t.gallery.place, [it.property_type ? t.types[it.property_type] : null, it.sector].filter(Boolean).join(" · "), "owner-detail-type")
                        : null}
                      {row("E-mail", it.email, "owner-detail-email")}
                      {row(t.admin.tenant_phone, it.phone || "—", "owner-detail-phone")}
                    </dl>
                    {it.message ? (
                      <p data-testid="owner-detail-message" className="mt-5 whitespace-pre-line text-sm italic leading-relaxed text-maquis-olive">« {it.message} »</p>
                    ) : null}
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <StatusBadge status={it.status || "EN_ATTENTE"} />
                      <a
                        data-testid="owner-detail-reply"
                        href={`mailto:${it.email}?subject=${encodeURIComponent(mailSubject)}`}
                        className="btn-outline-elegant inline-flex !px-4 !py-2 !text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                      >
                        {t.admin.reply}
                      </a>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
